// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { CONNECTION_STATUSES, DEVICE_EMPLOYEE_SYNC_STATUSES, DEVICE_STATUSES, LOG_LEVELS } from '../enums.js';
import { booleanQuerySchema, codeSchema, isoDateTimeSchema, jsonObjectSchema, paginationQuerySchema, timezoneSchema, uuidSchema } from '../common.js';
/**
 * PATCH schema derived from a create schema: every field optional and *without* its default. Zod 4 `.partial()` keeps
 * `.default(...)` wrappers, so a one-field PATCH would silently reset every defaulted column (AGENTS.md "Zod 4 pitfalls").
 */
export function updateSchemaOf(shape) {
    const out = {};
    for (const [key, field] of Object.entries(shape)) {
        const inner = field instanceof z.ZodDefault ? field.removeDefault() : field;
        out[key] = inner.optional();
    }
    return z.object(out);
}
/** Query for GET /device-providers: when `orgId` is given the list is filtered by that organisation's provider_* flags. */
export const deviceProvidersQuerySchema = z.object({ orgId: uuidSchema.optional() });
export const deviceModelsQuerySchema = z.object({ providerKey: z.string().min(1).max(64).optional() });
export const deviceListQuerySchema = paginationQuerySchema.extend({
    branchId: uuidSchema.optional(),
    /** A group / branch location → the devices of its branches; a place → the devices installed in it or below (docs/locations.md). */
    locationId: uuidSchema.optional(),
    status: z.enum(DEVICE_STATUSES).optional(),
    connectionStatus: z.enum([...CONNECTION_STATUSES, 'vendor_degraded']).optional(),
    providerKey: z.string().max(64).optional(),
    tag: z.string().max(40).optional(),
    groupId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
    includeDecommissioned: booleanQuerySchema.default(false),
});
export const deleteDeviceQuerySchema = z.object({ decommission: booleanQuerySchema.default(false) });
/** Fleet summary query — same branch scoping as the list. */
export const deviceSummaryQuerySchema = z.object({ branchId: uuidSchema.optional(), locationId: uuidSchema.optional(), includeDecommissioned: booleanQuerySchema.default(false) });
/** Counts for list headers / dashboards; keys of `byConnectionStatus` are CONNECTION_STATUSES (+ 'vendor_degraded'), of `byStatus` DEVICE_STATUSES. */
export const deviceSummaryDtoSchema = z.object({
    total: z.number().int(),
    byConnectionStatus: z.record(z.string(), z.number().int()),
    byStatus: z.record(z.string(), z.number().int()),
    /** active devices without any heartbeat in the last 24 hours (or never seen) */
    staleHeartbeats: z.number().int(),
});
/** Secret config fields keyed by provider config field (validated against the provider's `secretFields`). */
export const deviceCredentialsInputSchema = z.record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/), z.union([z.string().max(4096), z.number(), z.boolean()]))
    .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one credential field' });
export const deviceLogQuerySchema = paginationQuerySchema.extend({
    level: z.enum(LOG_LEVELS).optional(),
    event: z.string().max(80).optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
export const deviceCommandQuerySchema = paginationQuerySchema.extend({ status: z.enum(['pending', 'sent', 'acked', 'failed', 'expired']).optional() });
export const deviceEmployeeQuerySchema = paginationQuerySchema.extend({
    syncStatus: z.enum(DEVICE_EMPLOYEE_SYNC_STATUSES).optional(),
    desired: booleanQuerySchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const deviceGroupInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    branchId: uuidSchema.nullable().optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
});
export const deviceGroupMembersSchema = z.object({ deviceIds: z.array(uuidSchema).min(1).max(500) });
export const pendingDevicesQuerySchema = z.object({ serialNumber: z.string().trim().min(1).max(120).optional() });
export const claimPendingDeviceSchema = z.object({
    branchId: uuidSchema,
    name: z.string().trim().min(1).max(120),
    code: codeSchema,
    timezone: timezoneSchema.optional(),
    modelId: uuidSchema.optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export const deviceGroupDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    branchId: uuidSchema.nullable(),
    branchName: z.string().nullable().optional(),
    color: z.string().nullable(),
    deviceCount: z.number().int(),
    deviceIds: z.array(uuidSchema).optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const pendingDeviceDtoSchema = z.object({
    id: uuidSchema,
    providerKey: z.string(),
    serialNumber: z.string(),
    claimCode: z.string(),
    organizationId: uuidSchema.nullable(),
    firstSeenAt: isoDateTimeSchema,
    lastSeenAt: isoDateTimeSchema,
    remoteIp: z.string().nullable(),
    deviceInfo: jsonObjectSchema,
    claimedDeviceId: uuidSchema.nullable(),
});
export const deviceLogDtoSchema = z.object({
    id: z.string(),
    deviceId: uuidSchema,
    level: z.enum(LOG_LEVELS),
    event: z.string(),
    message: z.string().nullable(),
    details: jsonObjectSchema.nullable(),
    jobId: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const deviceCommandDtoSchema = z.object({
    id: uuidSchema,
    deviceId: uuidSchema,
    sequence: z.string(),
    commandType: z.string(),
    payload: jsonObjectSchema,
    status: z.enum(['pending', 'sent', 'acked', 'failed', 'expired']),
    syncJobItemId: uuidSchema.nullable(),
    result: jsonObjectSchema.nullable(),
    createdAt: isoDateTimeSchema,
    sentAt: isoDateTimeSchema.nullable(),
    ackedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema,
});
//# sourceMappingURL=devices.js.map